import React, { MouseEvent, PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';

import { BlockProgram } from '@/components/BlockProgram';
import { Icon } from '@/components/Icon';
import { usePlayerStartMedia } from '@/modules/player/providers/dispatch/usePlayerStartMedia';
import { Block, Media, Program } from '@/types';
import { convertDurationMin } from '@/utils';

export interface BlockProgramProps {
  media: Media;
  lastTimeInMedia: number;
  isListened?: boolean;
  program: Program;
  block?: Block;
  handleHideEpisode?: (UUID: string, e: React.MouseEvent) => void;
}

export const BlockMediaInProgress = ({
  media,
  lastTimeInMedia,
  isListened = false,
  program,
  block,
  handleHideEpisode,
}: PropsWithChildren<BlockProgramProps>) => {
  const { formatMessage } = useIntl();
  const { startMedia } = usePlayerStartMedia();

  if (!media) {
    return null;
  }

  const clickHideMedia = (e: MouseEvent) => {
    handleHideEpisode(media.UUID, e);
  };

  const episodeRemainingTimeLength = convertDurationMin(media?.duration - (lastTimeInMedia || 0));

  const handleStartMedia = () => {
    startMedia({
      media,
      lastTimeInMedia: lastTimeInMedia || 0,
      autoPlay: true,
    });
  };

  return (
    <div
      className="flex flex-col cursor-pointer"
      key={media.UUID}
      onClick={handleStartMedia}
      role="button"
      aria-label={formatMessage(
        {
          id: `catalogScreen.accessibility.program.${isListened ? 'restart' : 'continue'}`,
        },
        {
          episode: media.episode,
          episodeTitle: media.title,
          programTitle: program?.title,
        }
      )}
    >
      {program && (
        <div className="w-full">
          <BlockProgram program={program} media={media} block={block} />
        </div>
      )}
      {media && (
        <div className="flex flex-col justify-center p-[2%] w-full relative">
          <div className="flex justify-between">
            <div className="flex flex-col w-full min-w-[85%]">
              <h3 className="text-base text-white pt-[3px] pr-[20px] whitespace-nowrap text-ellipsis overflow-hidden w-full">
                {media.title}
              </h3>

              <p className="text-[12px] lg:text-xs py-1 text-brand-neutral-300 leading-normal">
                {formatMessage(
                  {
                    id: `common.program.linear.${
                      isListened
                        ? 'listened'
                        : media.duration > lastTimeInMedia
                        ? 'listening'
                        : 'listened'
                    }.description`,
                  },
                  {
                    episode: media.episode,
                    duration:
                      !isListened &&
                      formatMessage(
                        { id: 'program.media.listening.progress' },
                        { timeLength: episodeRemainingTimeLength }
                      ),
                  }
                )}
              </p>
            </div>

            <button
              className="button-hide-media rounded-full border mt-3 flex items-center hover:bg-brand-neutral-700 hover:bg-opacity-90 hover:border-opacity-50 aspect-square w-fit h-fit self-end"
              onClick={clickHideMedia}
              aria-label={formatMessage(
                { id: 'library.accessibility.deleteEpisode' },
                { episode: media.episode, episodeTitle: media.title }
              )}
            >
              <span className="popover-hide-media bg-white absolute hidden text-xs py-1 px-2 rounded z-30 right-3 text-center top-10 sm:top-12 drop-shadow text-brand-neutral-800">
                {formatMessage({ id: 'library.history.deleteEpisode' })}
              </span>
              <Icon name="less" className="sm:text-2xl text-xl px-[5px] sm:px-[7px]" />
            </button>
          </div>

          <div className="w-[70%] bg-white bg-opacity-50 m-0 overflow-hidden relative h-[4px] md:h-[6px] mt-[6px] rounded">
            <div
              className="bg-primary h-[4px] md:h-[6px]"
              style={{
                width: `${
                  (isListened ? media.duration * 100 : lastTimeInMedia * 100) / media.duration
                }%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
